import {Injectable} from '@angular/core';
import {DashboardHttpService} from '../../../core-services/dashboard-http-service/dashboard-http.service';
import {environment} from '../../../../environments/environment';
import {
  AssociatedTagEntities,
  CreateTagsRequest,
  EntityGroupName,
  MultipleTagAssociationEntity,
  NestedTag,
  Tag,
  TagExclusiveOption,
  UsedTag
} from '../../dtos/api';
import {Observable} from 'rxjs';
import {map, shareReplay, switchMap} from 'rxjs/operators';
import {outPlaceSort} from '../../pure-utils/array-utils';
import {DashboardApiService} from './dashboard-api.service';

@Injectable({
  providedIn: 'root',
})
export class TagApiService {
  private path = '/tags';

  constructor(private http: DashboardHttpService, private dashboardApiService: DashboardApiService) {
  }

  usersTagGroups$ = this.dashboardApiService.currentAccount$.pipe(
    switchMap(currentAccount => this.getNestedTagsByEntity(currentAccount.entity_id, currentAccount.entity_group_name)),
    map(nestedTags => this.flattenTags(nestedTags)),
    map<NestedTag[], TagExclusiveOption[]>(flattenedTags => {
      flattenedTags = flattenedTags.map(x => ({...x, filterType: 'tag_groups'}));
      return <TagExclusiveOption[]> outPlaceSort(flattenedTags, x => x.tag_name);
    }),
    shareReplay({bufferSize: 1, refCount: true})
  );

  flattenTags = (nestedTags) => nestedTags.reduce((flattenedTags, singleNestedTags) => {
    if (singleNestedTags.children.length === 0) {
      return flattenedTags.concat(singleNestedTags);
    } else {
      return flattenedTags.concat(this.flattenTags(singleNestedTags.children));
    }
  }, <NestedTag[]>[]);

  createTag(requestBody: CreateTagsRequest): Observable<Tag> {
    return this.http.postWithOptions(`${environment.apiUrl}${this.path}`, requestBody);
  }

  associateMultipleTagsToEntity(entityId: string, entityGroupName: 'merchant_ads' | 'merchant_ads_schedules', tagUuids: string[]) {
    let requestBody = {
      tag_ids: tagUuids
    };
    let url = `${environment.apiUrl}${this.path}/entity/${entityId}/${entityGroupName}/multiple`;
    return this.http.postWithOptions<MultipleTagAssociationEntity[]>(url, requestBody);
  }

  disassociateMultipleTagsFromEntity(entityId: string, entityGroupName: 'merchant_ads' | 'merchant_ads_schedules', tagUuids: string[]) {
    let url = `${environment.apiUrl}${this.path}/${tagUuids.join(',')}/entity/${entityId}/${entityGroupName}/multiple`;

    return this.http.delete<MultipleTagAssociationEntity[]>(url);
  }

  userUsedTags$ = this.http.getWithOptions<UsedTag[]>(`${environment.apiUrl}${this.path}/entity`);

  getUserUsedTags(entityGroup: 'affiliates' | 'merchants', entityId: string) {
    return this.http.getWithOptions<UsedTag[]>(`${environment.apiUrl}/${entityGroup}/${entityId}/ads${this.path}`);
  }

  getNestedTagsByEntity(entityId: string, entityGroup: EntityGroupName) {
    return this.http.getWithOptions<NestedTag[]>(`${environment.apiUrl}${this.path}/entity/${entityId}/${entityGroup}`);
  }

  getAssociatedEntitiesOfTag(entityId: string, entityGroup: EntityGroupName) {
    return this.http.getWithOptions<AssociatedTagEntities[]>(`${environment.apiUrl}${this.path}/entity/${entityId}/${entityGroup}/relationships`);
  }

  getTagsByType(tag_type: string, is_deleted = false) {
    let isDeletedParam = '';
    if(is_deleted) {
      isDeletedParam = '&is_deleted=true';
    };
    return this.http.getWithOptions(`${environment.apiUrl}${this.path}/formatted?tag_type=${tag_type}${isDeletedParam}`);
  }
}
